import { useMemo, useRef, useState } from 'react';

import { isValidCageId } from '@/domain/cages';
import { useBiomassController_GetFeedingConversionRate } from '@/services/hooks';

import { isRefreshPollingSlow, refreshPollInterval } from '../feeding/liveRefreshPolling';

import { mapFeedConversionRateToDomain } from './mappers';
import type {
    UseGetFeedConversionRateOptions,
    UseGetFeedConversionRateParams,
    UseGetFeedConversionRateResult,
} from './types';

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const DEFAULT_WEIGHT_UNIT_TAG = 'g';

/**
 * Fetches the biological feed conversion ratio series for a cage.
 *
 * The query only runs once both dates are known. The endpoint requires them, and
 * the legacy call site sent `undefined` while the page's display range was still
 * being hydrated, producing a request the backend rejected.
 *
 * `options.enabled` can only narrow when the query runs, never widen it.
 */
export const useGetFeedConversionRate = (
    cageId: number | undefined,
    params: UseGetFeedConversionRateParams,
    options?: UseGetFeedConversionRateOptions
): UseGetFeedConversionRateResult => {
    const hasValidCageId = cageId != null && isValidCageId(cageId);
    const hasRange = !!params.from && !!params.to;

    const resolvedCageId = cageId ?? 0;
    const resolvedFrom = params.from ?? '';
    const resolvedTo = params.to ?? '';
    const resolvedWeightUnitTag = params.weightUnitTag ?? DEFAULT_WEIGHT_UNIT_TAG;

    const [pollingSlow, setPollingSlow] = useState(false);

    // These four values are exactly the inputs the generated hook below builds its
    // own react-query key from (see `BiomassController_GetFeedingConversionRate.info`
    // in `services/hooks.ts`), so this signature changes exactly when that key does.
    // A different cage or date range gets a fresh `Query` instance and its own poll
    // budget, so a flag raised for the previous identity must not carry over. This is
    // React's documented pattern for adjusting state in response to a derived value
    // changing, applied during render rather than in an effect.
    const keySignature = JSON.stringify([
        resolvedCageId,
        resolvedFrom,
        resolvedTo,
        resolvedWeightUnitTag,
    ]);
    const prevKeySignatureRef = useRef(keySignature);
    if (prevKeySignatureRef.current !== keySignature) {
        prevKeySignatureRef.current = keySignature;
        setPollingSlow(false);
    }

    const query = useBiomassController_GetFeedingConversionRate(
        resolvedCageId,
        {
            from: resolvedFrom,
            to: resolvedTo,
            weight_unit_tag: resolvedWeightUnitTag,
        },
        {
            staleTime: FIVE_MINUTES_MS,
            ...options,
            enabled: hasValidCageId && hasRange && (options?.enabled ?? true),
            // `dataUpdateCount` and `errorUpdateCount` are react-query's own counts of
            // settled fetches (successful and failed respectively) for this query
            // identity; `refreshPollInterval` and `isRefreshPollingSlow` treat their sum
            // the same way the original single-cap version did (see their own doc
            // comments for why failures count). This callback runs outside React's
            // render phase (query timer/update callbacks), so setting `pollingSlow`
            // here does not conflict with rendering.
            refetchInterval: (activeQuery) => {
                const refreshing = activeQuery.state.data?.data.refreshing ?? false;
                const settledFetches =
                    activeQuery.state.dataUpdateCount + activeQuery.state.errorUpdateCount;
                if (refreshing && isRefreshPollingSlow(settledFetches)) {
                    setPollingSlow(true);
                }
                return refreshPollInterval(refreshing, settledFetches);
            },
            refetchIntervalInBackground: false,
        }
    );

    const data = useMemo(() => mapFeedConversionRateToDomain(query.data?.data), [query.data?.data]);

    return {
        ...query,
        isLoading: query.isPending,
        data,
        pollingSlow,
    };
};
